/**
 * ToastProvider adapter for apps/dashboard.
 *
 * After the Phase Zero decommission (FR-007), the legacy ToastContext at
 * src/components/cross-cutting/ToastContext is deleted. This module provides
 * a minimal inline implementation using custom DOM events.
 *
 * The PRD component code calls:
 *   const { showToast } = useToast();
 *   showToast({ message: "...", kind: "success" });
 *
 * The ToastProvider wrapper component renders children and listens for
 * capicua:toast events to render toast notifications. Used as the outermost
 * provider in the AppShell composition root (specs/integration-plan.md 6.4).
 */
import React, { type ReactNode } from "react";

interface ShowToastArgs {
  message: string;
  kind: "success" | "error" | "warning" | "info";
}

export function useToast(): { showToast: (args: ShowToastArgs) => void } {
  function showToast({ message, kind }: ShowToastArgs): void {
    // Dispatch via the capicua:toast custom event pattern (FR-013).
    // See apps/dashboard/src/api/utils/toast.ts for the emitToast helper.
    if (typeof document !== "undefined") {
      document.dispatchEvent(
        new CustomEvent("capicua:toast", {
          detail: { message, kind },
        }),
      );
    }
  }

  return { showToast };
}

/**
 * ToastProvider -- wraps children and provides the toast event context.
 * Outermost provider in the AppShell composition root.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
